import { autoToast } from '../stores/toast';
import { upgradePrompt } from '../stores/plan';
import { PlanFeatureSchema } from '@codot/contracts';
import { apiPath, ApiErrorBodySchema, type CompileIssue, CompileFailureSchema } from '@codot/contracts';
import type { z } from 'zod';
import { i18n, ui } from '../i18n';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** Machine-readable `ApiErrorCode` when the API supplied one (QUOTA_SCREENS_EXCEEDED, …). */
  readonly code: string | undefined;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    const parsed = ApiErrorBodySchema.safeParse(body);
    this.code = parsed.success ? parsed.data.code : undefined;
  }
}

/** Human-friendly message from any error (API message → fallback). */
export function errorMessage(err: unknown, fallback?: string): string {
  const fb = fallback ?? i18n.global.t('common.somethingWrong');
  if (err instanceof ApiError) {
    // Phase 17.7: known codes in the person's language; English keeps the server's (more specific) wording.
    if (ui.lang !== 'en' && err.code && i18n.global.te(`errors.${err.code}`)) return i18n.global.t(`errors.${err.code}`);
    const body = err.body as { message?: unknown; issues?: { message: string }[] } | undefined;
    if (body?.issues?.length) return body.issues.map((i) => i.message).join(' · ');
    if (typeof body?.message === 'string') return body.message;
  }
  return err instanceof Error ? err.message : fb;
}

/* ------------------------------------------------------------------ *
 * Session refresh: the access cookie lives 15 min. On a 401 we refresh
 * ONCE (single-flight per tab + Web Locks across tabs) and retry.
 *
 * Only an answer from the API that the session is over signs the person out. When the API can't be reached
 * (restarting, deploying, network drop) the refresh is retried for a while and the request fails with
 * "can't reach the server", but the person stays signed in.
 * ------------------------------------------------------------------ */

type RefreshResult = 'ok' | 'denied' | 'unavailable';
let refreshing: Promise<RefreshResult> | null = null;
/** Retries while the API is unreachable: about 20 s in all. */
const REFRESH_RETRY_MS = [1_000, 2_000, 4_000, 6_000, 8_000];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** The request couldn't reach the API (or the dev proxy answered for it): not a signal about the session. */
export function serverUnavailable(): ApiError {
  return new ApiError(i18n.global.t('common.serverUnavailable'), 503, { message: i18n.global.t('common.serverUnavailable') });
}
let onSessionExpired: () => void = () => {};

/** Wired by main.ts → sends the user to /login when the session can't be refreshed. */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

async function refreshOnce(): Promise<RefreshResult> {
  try {
    const res = await fetch(apiPath('authRefresh'), { method: 'POST', credentials: 'same-origin' });
    if (res.ok) return 'ok';
    // 401/403: the API says the session is over. Anything else (502/503/504 from a proxy, 500): try again.
    return res.status === 401 || res.status === 403 ? 'denied' : 'unavailable';
  } catch {
    return 'unavailable'; // network error: the API isn't reachable
  }
}

async function doRefresh(): Promise<RefreshResult> {
  const run = async () => {
    let result = await refreshOnce();
    for (const wait of REFRESH_RETRY_MS) {
      if (result !== 'unavailable') break;
      await sleep(wait);
      result = await refreshOnce();
    }
    return result;
  };
  // Serialize across tabs so two tabs never present the same refresh token concurrently.
  if (typeof navigator === 'undefined' || !('locks' in navigator)) return run();
  // A tab that fell asleep holding the lock (laptop lid closed mid-refresh) must not block this one forever.
  return navigator.locks.request('codot-auth-refresh', { signal: AbortSignal.timeout(20_000) }, run).catch((err: unknown) =>
    err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError') ? run() : Promise.reject(err),
  );
}

function refresh(): Promise<RefreshResult> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** True when the session was refreshed (uploads retry their request); false otherwise, never signing out. */
export async function refreshSession(): Promise<boolean> {
  return (await refresh()) === 'ok';
}

const NO_RETRY = new Set([apiPath('authLogin'), apiPath('authRefresh'), apiPath('authSignup'), apiPath('authLogout')]);
/**
 * The bootstrap probe (`GET /auth/me`) may legitimately 401 on public pages (login, invite links);
 * the auth store turns that into "anonymous" — it must NOT trigger the global redirect-to-login.
 */
const NO_EXPIRY_REDIRECT = new Set([apiPath('authMe')]);

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Every CMS request goes through here: same-origin cookies, JSON, contract validation of the
 * response, typed ApiError on failure, transparent refresh on 401.
 */
export async function request<TSchema extends z.ZodType>(
  method: Method,
  path: string,
  schema: TSchema | null,
  body?: unknown,
  retried = false,
): Promise<z.output<TSchema>> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw serverUnavailable();
  }

  if (res.status === 401 && !retried && !NO_RETRY.has(path)) {
    const result = await refresh();
    if (result === 'ok') return request(method, path, schema, body, true);
    // Can't tell (the API is down): stay signed in; the caller shows the error and the next action tries again.
    if (result === 'unavailable') throw serverUnavailable();
    if (!NO_EXPIRY_REDIRECT.has(path)) onSessionExpired();
  }

  const payload: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    // Phase 17.10: a locked feature → the upgrade prompt (the error still reaches the caller).
    const feature = (payload as { code?: string; feature?: string } | null)?.code === 'PLAN_FEATURE_REQUIRED' ? PlanFeatureSchema.safeParse((payload as { feature?: string }).feature) : null;
    if (feature?.success) upgradePrompt.value = feature.data;
    throw new ApiError(`${method} ${path} → ${res.status}`, res.status, payload);
  }
  if (method !== 'GET') autoToast(method, path);
  if (schema === null) return undefined as z.output<TSchema>;

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(`Response from ${path} violates contract: ${parsed.error.message}`, res.status, payload);
  }
  return parsed.data;
}

/** Phase 1/2 helper, kept for existing callers. */
export function getJson<TSchema extends z.ZodType>(path: string, schema: TSchema): Promise<z.output<TSchema>> {
  return request('GET', path, schema);
}

/** Hex SHA-256 via WebCrypto (available on https and localhost). */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Issues of a 422 MANIFEST_COMPILE_FAILED (compiler / decoder-budget rejections), else []. */
export function compileIssues(err: unknown): CompileIssue[] {
  if (!(err instanceof ApiError) || err.status !== 422) return [];
  const parsed = CompileFailureSchema.safeParse(err.body);
  return parsed.success ? parsed.data.issues : [];
}
